import IconButton from '@/components/buttons/IconButton'
import LoadMoreButton, { useLoadMore } from '@/components/buttons/LoadMoreButton'
import { AppLayout } from '@/components/Layouts/AppLayout'
import VideoDrawPlayer from '@/components/Layouts/VideoDrawPlayer'
import MonthPicker, { formatAsQry, useMonthPicker } from '@/components/MonthPicker'
import { useLanguage, useLayout } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { Format } from '@/utils/Format'
import { svgs } from '@/utils/Images'
import { appUtils } from 'lib/AppUtils'
import { FC, Fragment, memo, useEffect } from 'react'
import useSWRImmutable from 'swr/immutable'

export default function Draws() {
  const {
    errors: { no_data },
  } = useLanguage()
  const { isDesktop } = useLayout()

  const { pageIndex, total, setTotal, list, setList, resetParams, loadMore } = useLoadMore()

  const { filter, setFilter, onApply, onReset } = useMonthPicker(resetParams)

  const { data, mutate, isValidating, isLoading } = useSWRImmutable(
    typeof isDesktop != 'undefined'
      ? `${API_URL.draws}?perPage=${isDesktop === false ? '6' : '12'}&page=${pageIndex + 1}${filter ? '&sort_by_month=' + formatAsQry(filter) : ''}`
      : null,
    appUtils.fetcher
  )

  useEffect(() => {
    if (!isValidating && !isLoading && data?.data?.length > 0) {
      setList(prev => data?.data?.reduce?.((arr, item) => arr.concat(item), prev))
      setTotal(data?.total)
    }
  }, [isValidating, isLoading, data])

  return (
    <AppLayout
      showBanner
      title='draws'
      titleOuterCls='sticky top-16 md:top-20 z-[51]' // z-index must be larger than LoadingView's
      titleInnerCls='flex items-center'
      filter={
        <Fragment>
          <MonthPicker valueCtrl={[filter, setFilter]} onApply={onApply} className='ml-auto' />
          <IconButton
            className='ml-2 h-8 w-8 p-1.5 md:ml-4'
            onClick={() =>
              onReset().then(() => {
                if (!isValidating && !isLoading) {
                  mutate()
                }
              })
            }
          >
            {svgs.reload}
          </IconButton>
        </Fragment>
      }
      outerCls='relative'
      innerCls='grid w-full animate-fade-in-down grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6'
    >
      {list?.length > 0 ? list?.map((item, index) => <Item key={'row-' + index} data={item} />) : null}

      {isValidating || isLoading ? (
        <Skeletons />
      ) : data?.data?.length > 0 ? null : (
        <div className='col-span-full row-span-full animate-fade-in-down place-self-center py-24 text-center'>{no_data}</div>
      )}

      <LoadMoreButton className='col-span-full h-10 justify-self-center' onClick={loadMore} current={list?.length} total={total} />
    </AppLayout>
  )
}

const Item: FC<{ data: Record<string, any> }> = ({ data }) => {
  return (
    <div className='card-container flex animate-fade-in-down flex-col pb-2 text-base duration-1000'>
      <VideoDrawPlayer url={data.url} iframe={data.iframe} className='lg:h-60' />
      <span className='mt-2 h-6 text-center font-semibold lg:mt-1'>{Format.dateMonthYearFormat(data.draw_date)}</span>
    </div>
  )
}

const Skeletons: FC<{ numOfItems?: number }> = memo(function Skeleton({ numOfItems = 12 }) {
  const skeletons = []
  for (let index = 0; index < numOfItems; index++) {
    skeletons.push(
      <div key={index} className='card-container animate-fade-in-down'>
        <div className='skeleton-wrap items-center'>
          <div className='skeleton-img-wrap aspect-video w-full rounded-t-lg lg:h-60'>
            <svg className='skeleton-img' xmlns='http://www.w3.org/2000/svg' fill='currentColor' viewBox='0 0 16 20'>
              <path d='M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z' />
              <path d='M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM9 13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2Zm4 .382a1 1 0 0 1-1.447.894L10 13v-2l1.553-1.276a1 1 0 0 1 1.447.894v2.764Z' />
            </svg>
          </div>
          <div className='skeleton-line m-4 h-3 w-1/2 sm:px-3'></div>
        </div>
      </div>
    )
  }
  return <Fragment>{skeletons}</Fragment>
})
